import { Controller } from "@hotwired/stimulus"
import hugerte from "hugerte"
import { registerCustomPreviewPlugin } from "../hugerte/plugins/custom_preview_plugin"
import 'hugerte/models/dom'
import 'hugerte/icons/default'
import 'hugerte/themes/silver'
import 'hugerte/skins/ui/oxide/skin.js'
import 'hugerte/skins/ui/oxide/content.js'
import 'hugerte/skins/content/default/content.js'
import 'hugerte/plugins/visualblocks'
import 'hugerte/plugins/visualchars'
import 'hugerte/plugins/link'
import 'hugerte/plugins/lists'
import 'hugerte/plugins/code'
import 'hugerte/plugins/image'
import 'hugerte/plugins/searchreplace'
import 'hugerte/plugins/fullscreen'
import 'hugerte/plugins/wordcount'
import 'hugerte/plugins/help'
import 'hugerte/plugins/help/js/i18n/keynav/en.js'
import 'hugerte/plugins/quickbars'
import 'hugerte/plugins/autoresize'

export default class extends Controller {
  static targets = ["editor"]
  static values = {
    height: Number,
    toolbar: String,
    plugins: String,
    ownerType: String,
    ownerId: String,
    customStyles: String,
    useMediaLibrary: { type: Boolean, default: false }
  }

  connect() {
    this.initializeEditors()
  }

  editorTargetConnected(element) {
    // Initialize new editors when they are dynamically added
    this.initializeEditor(element)
  }

  initializeEditors() {
    this.editorTargets.forEach(element => {
      this.initializeEditor(element)
    })
  }

  initializeEditor(element) {
    // Skip if already initialized
    if (element.dataset.hugerteInitialized === 'true') {
      return
    }

    const imageButton = this.useMediaLibraryValue ? 'media_library' : 'image'
    const insertMenuItems = this.useMediaLibraryValue ? 'media_library link | hr' : 'image link | hr'
    const quickbarsToolbar = this.useMediaLibraryValue ? 'quicklink' : 'quickimage quicklink'

    // Generate unique ID for the element if it doesn't have one
    if (!element.id) {
      element.id = `hugerte-${Math.random().toString(36).substr(2, 9)}`
    }

    hugerte.init({
      target: element,
      min_height: this.heightValue || (element.value.trim() ? 900 : 400),
      max_height: 900,
      menubar: true,
      menu: {
        file: { title: 'File', items: 'newdocument | preview | export print' },
        edit: { title: 'Edit', items: 'undo redo | cut copy paste pastetext | selectall | searchreplace' },
        view: { title: 'View', items: 'code | visualaid visualchars visualblocks | preview fullscreen' },
        insert: { title: 'Insert', items: insertMenuItems },
        format: { title: 'Format', items: 'bold italic underline strikethrough superscript subscript codeformat | styles blocks fontfamily fontsize align lineheight | forecolor backcolor | removeformat' },
        tools: { title: 'Tools', items: 'code wordcount' },
        help: { title: 'Help', items: 'help' }
      },
      toolbar: `undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | ${imageButton} link`,
      plugins: this.pluginsValue || 'link lists code image searchreplace fullscreen wordcount help visualblocks visualchars quickbars autoresize',
      branding: false,
      quickbars_insert_toolbar: quickbarsToolbar,
      statusbar: true,
      images_upload_url: '/admin/asset_manager/rich',
      images_upload_credentials: true,
      relative_urls: false,
      images_upload_handler: (blobInfo, progress) => this.imageUploadHandler(blobInfo, progress),
      skin_url: 'default',
      content_css: false,
      content_style: 'body { width: 600px; padding-left: 100px; padding-right: 100px; } img { max-width: 600px; height: auto; }',
      document_base_url: '/',
      images_file_types: 'jpeg,jpg,jpe,png',
      style_formats_merge: true,
      style_formats: this.buildStyleFormats(),
      setup: (editor) => {
        this.editor = editor

        if (this.useMediaLibraryValue) {
          // Add media library button and hide default image plugin button
          editor.ui.registry.addButton('media_library', {
            icon: 'image',
            tooltip: 'Insert image from gallery',
            onAction: () => {
              this.openMediaLibrary(editor)
            }
          })

          // Hide the default image button from plugin
          editor.ui.registry.addButton('image', {
            icon: 'image',
            tooltip: 'Insert image',
            onAction: () => {
              // Do nothing or show a message
            },
            onSetup: (buttonApi) => {
              buttonApi.setDisabled(true)
              return () => {}
            }
          })
        }

        // Unwrap <p><img/></p> → <img/> after image insertion
        editor.on('ExecCommand', (e) => {
          if (['mceInsertContent', 'mceUpdateImage'].includes(e.command)) {
            setTimeout(() => this.unwrapLoneImages(editor), 0)
          }
        })

        // Strip <p> wrappers around lone images when content is serialized (saved)
        editor.on('GetContent', (e) => {
          if (e.format === 'html') {
            e.content = e.content.replace(/<p>(\s*(?:<img[^>]+>\s*)+)<\/p>/gi, '$1')
          }
        })

        // Inject custom styles when editor is ready
        editor.on('init', () => {
          this.injectCustomStyles(editor)

          if (this.useMediaLibraryValue) {
            this.setupMediaLibraryListener(editor)
          }
        })

        // Register custom preview plugin
        registerCustomPreviewPlugin(editor, () => this.getCustomStylesCSS())
      }
    }).then(() => {
      // Mark as initialized
      element.dataset.hugerteInitialized = 'true'
    })
  }

  disconnect() {
    // Remove all hugerte instances
    hugerte.remove()

    // Clean up event listener
    if (this.mediaLibraryHandler) {
      window.removeEventListener('media-library-modal:selectAsset', this.mediaLibraryHandler)
    }
  }

  buildStyleFormats() {
    if (!this.hasCustomStylesValue || this.customStylesValue === "") {
      return []
    }

    try {
      const customStyles = JSON.parse(this.customStylesValue)

      // Ensure customStyles is an array
      if (!Array.isArray(customStyles)) {
        console.warn('Custom styles is not an array:', customStyles)
        return []
      }

      // Create a "Styles" section with custom styles
      return [
        {
          title: 'Styles',
          items: customStyles.map(cssClass => ({
            title: cssClass.name,
            selector: '*',
            classes: cssClass.name,
            wrapper: false
          }))
        }
      ]
    } catch (e) {
      console.error('Error parsing custom styles:', e)
      return []
    }
  }

  getCustomStylesCSS() {
    if (!this.hasCustomStylesValue || this.customStylesValue === "") {
      return ""
    }

    try {
      const customStyles = JSON.parse(this.customStylesValue)

      // Ensure customStyles is an array
      if (!Array.isArray(customStyles)) {
        console.warn('Custom styles is not an array:', customStyles)
        return ""
      }

      return customStyles.map(cssClass =>
        `.${cssClass.name} { ${cssClass.styles} }`
      ).join(' ')
    } catch (e) {
      console.error('Error generating custom styles CSS:', e)
      return ""
    }
  }

  injectCustomStyles(editor) {
    if (!this.hasCustomStylesValue || this.customStylesValue === "") {
      return
    }

    try {
      const customStyles = JSON.parse(this.customStylesValue)

      // Ensure customStyles is an array
      if (!Array.isArray(customStyles)) {
        console.warn('Custom styles is not an array:', customStyles)
        return
      }

      let styleTag = '<style id="hugerte-custom-styles">'

      customStyles.forEach(cssClass => {
        styleTag += `.${cssClass.name} { ${cssClass.styles} }`
      })

      styleTag += '</style>'

      // Add the styles to the editor's document head
      const editorDoc = editor.getDoc()
      editorDoc.head.insertAdjacentHTML('beforeend', styleTag)
    } catch (e) {
      console.error('Error injecting custom styles:', e)
    }
  }

  openMediaLibrary(editor) {
    this.currentEditor = editor

    // Dispatch event to open media library modal
    window.dispatchEvent(new CustomEvent('media-library:showGallery', {
      detail: { mediaType: 'Image' }
    }))
  }

  setupMediaLibraryListener(editor) {
    // Listen for asset selection from media library modal
    this.mediaLibraryHandler = (event) => {
      const { assetUrl, assetName } = event.detail

      // Only insert if this is the editor that requested the image
      if (this.currentEditor === editor) {
        // Insert image into the editor without wrapping p tag
        const img = this.currentEditor.getDoc().createElement('img')
        img.src = assetUrl
        img.alt = assetName || ''
        img.style.cssText = 'max-width: 100%; height: auto;'
        img.width = 600
        const insertingEditor = this.currentEditor
        insertingEditor.selection.setNode(img)
        setTimeout(() => this.unwrapLoneImages(insertingEditor), 0)

        // Clear the current editor reference after insertion
        this.currentEditor = null
      }
    }

    window.addEventListener('media-library-modal:selectAsset', this.mediaLibraryHandler)
  }

  unwrapLoneImages(editor) {
    editor.getBody().querySelectorAll('p').forEach(p => {
      const meaningfulChildren = Array.from(p.childNodes).filter(n =>
        !(n.nodeType === 3 && !n.textContent.trim()) &&
        !(n.nodeType === 1 && n.getAttribute('data-mce-bogus'))
      )
      if (meaningfulChildren.length > 0 && meaningfulChildren.every(n => n.nodeName === 'IMG')) {
        meaningfulChildren.forEach(img => p.parentNode.insertBefore(img, p))
        p.parentNode.removeChild(p)
      }
    })
  }

  imageUploadHandler = (blobInfo, progress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.withCredentials = true
    xhr.open('POST', '/admin/asset_manager/rich')
    const csrfToken = document.querySelector('meta[name=csrf-token]').getAttribute('content')
    xhr.setRequestHeader('X-CSRF-Token', csrfToken)

    xhr.upload.onprogress = (e) => {
      progress(e.loaded / e.total * 100)
    }

    xhr.onload = () => {
      if (xhr.status === 403) {
        let message = 'You do not have sufficient storage capacity on your account. Please contact Situate on support@situate.io.'
        try {
          const json = JSON.parse(xhr.responseText)
          message = json?.errors?.file?.[0] || message
        } catch (e) { /* fall back to default message */ }
        reject({ message, remove: true });
        return
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        reject('HTTP Error: ' + xhr.status)
        return
      }

      const json = JSON.parse(xhr.responseText)

      if (!json || typeof json.location != 'string') {
        reject('Invalid JSON: ' + xhr.responseText)
        return
      }

      resolve(json.location)
    }

    xhr.onerror = () => {
      reject('Image upload failed due to a XHR Transport error. Code: ' + xhr.status)
    }

    const formData = new FormData()
    formData.append('file', blobInfo.blob(), blobInfo.filename())

    formData.append('owner_type', this.ownerTypeValue)
    formData.append('owner_id', this.ownerIdValue)

    xhr.send(formData)
  })
}
